//#region IMPORTS
import {
  CloudArrowUpIcon,
  CloudArrowDownIcon,
} from "@heroicons/react/24/outline";
import {
  InstoreTemplate,
  InstoreTemplateTableRow,
} from "../../models/instoreTemplate";
import {
  CREATE_TEMPLATE_ROW_MODAL,
  UPDATE_TEMPLATE_SPECS_MODAL,
} from "../../models/_consts";
import { useExportInstoreOrderTemplateToExcel } from "../../hooks/useExportInstoreOrderTemplateToExcel";
import { useAppDispatch, useAppSelector } from "../../store/configureStore";
import { instoreTemplateRowSelectors } from "../../features/instoreTemplates/instoreTemplatesSlice";
import { useLocalTemplateRows } from "../../hooks/useLocalTemplateRows";
import { PencilSquareIcon } from "@heroicons/react/20/solid";
import { InstoreTheme } from "../../models/instoreTheme";
import { openModal } from "../../features/dashBoard/dashboardSlice";
import { Fragment } from "react";
import { toast } from "react-toastify";
import InstoreTemplateDetailTable from "./InstoreTemplateDetailTable";
import InstoreTemplateRowForm from "./InstoreTemplateRowForm";
import InstoreTemplateForm from "../instoreTemplates/InstoreTemplateForm";
import useAdminManager from "../../hooks/useAdminManager";
import MainContainer from "../_layout/MainContainer";
import GoBackButton from "../genericButtons/goBackButton/GoBackButton";
import useHQmanager from "../../hooks/useHQmanager";
import PageHeader from "../genericPageHeader/PageHeader";
import classNames from "../../helpers/classNames";
import MainModal from "../_layout/MainModal";
//#endregion

//#region INTERFACE
interface Props {
  instoreTemplate?: InstoreTemplate;
  create?: boolean;
  copy?: boolean;
}
//#endregion

export default function InstoreTemplateDetailContainer({
  instoreTemplate,
  create = false,
  copy = false,
}: Props) {
  //#region SETUP
  // Redux
  const dispatch = useAppDispatch();
  const { loading: loadingThemes } = useAppSelector(
    ({ instoreThemes }) => instoreThemes
  );
  const { status, loading } = useAppSelector(
    ({ instoreTemplates }) => instoreTemplates
  );

  const instoreTemplateRows = useAppSelector(
    instoreTemplateRowSelectors.selectAll
  );

  const { user } = useAppSelector(({ account }) => account);

  // Roles
  const isAdmin = useAdminManager(user);
  const hqTeam = useHQmanager(user);

  // Hooks
  const {
    distinctStoreFormats,
    moveRowAnywhere,
    localTableData,
    updateRowCells,
    addArticleRow,
    saveChanges,
    addThemeRow,
    removeRow,
    isDirty,
  } = useLocalTemplateRows(instoreTemplate!);

  const { exportTemplateToExcel, exporting } =
    useExportInstoreOrderTemplateToExcel();
  //#endregion

  //#region ADD THEME ROW
  const addThemeRowModalClickHandler = () => {
    dispatch(openModal(CREATE_TEMPLATE_ROW_MODAL));
  };

  const addThemeRowHandler = (theme: InstoreTheme) => {
    addThemeRow(theme);

    // Close modal
    dispatch(openModal(false));
  };
  //#endregion

  //#region ADD ARTICLE ROW
  const addArticleRowHandler = (themeId: number) => {
    addArticleRow(themeId);
  };
  //#endregion

  //#region MOVE ROW
  const moveRowHandler = (toIndex: number, row: InstoreTemplateTableRow) => {
    if (row && row.flatIndex !== undefined) {
      moveRowAnywhere(row.flatIndex, toIndex);
    } else {
      toast.error("Cannot move row: missing index");
    }
  };
  //#endregion

  //#region UPDATE ROW CELLS
  const updateRowCellsHandler = (
    columnId: string,
    value: any,
    row: InstoreTemplateTableRow
  ) => {
    updateRowCells(columnId, value, row);
  };
  //#endregion

  //#region REMOVE ROW
  const removeRowHandler = (row: InstoreTemplateTableRow) => {
    removeRow(row);
  };
  //#endregion

  //#region SAVE CHANGES
  const saveChangesHandler = async () => {
    await saveChanges();
  };
  //#endregion

  //#region EXPORT TEMPLATE
  const exportHandler = async (template: InstoreTemplate) => {
    await exportTemplateToExcel(template);
  };
  //#endregion

  //#region UPDATE SPECS
  const updateSpecsModalHandler = () => {
    dispatch(openModal(UPDATE_TEMPLATE_SPECS_MODAL));
  };
  //#endregion

  //#region CLEANUP UI
  const cancelCRUDTemplate = () => {
    dispatch(openModal(false));
  };
  //#endregion

  //#region RENDER
  return (
    <Fragment>
      {/* MODALS */}
      <MainModal
        type={CREATE_TEMPLATE_ROW_MODAL}
        closeHandler={cancelCRUDTemplate}
      >
        <InstoreTemplateRowForm
          addThemeRowHandler={addThemeRowHandler}
          usedThemes={Array.from(
            new Set(
              (instoreTemplateRows ?? [])
                .map((r: any) => (r.theme ?? "").toLowerCase())
                .filter(Boolean)
            )
          )}
          cancel={cancelCRUDTemplate}
        />
      </MainModal>

      <MainModal
        type={UPDATE_TEMPLATE_SPECS_MODAL}
        closeHandler={cancelCRUDTemplate}
      >
        <InstoreTemplateForm
          instoreTemplateId={instoreTemplate?.id}
          cancelCUD={cancelCRUDTemplate}
          disabled={
            loading &&
            (status.includes("PendingCreateInstoreTemplate") ||
              status.includes("PendingUpdateInstoreTemplate"))
          }
        />
      </MainModal>

      {/* BODY */}
      <MainContainer>
        <PageHeader
          extraHQButtons={
            <div className="flex items-center gap-4">
              {isAdmin && instoreTemplate ? (
                <button
                  disabled={loadingThemes || loading || exporting}
                  className={classNames(
                    "bg-pink-100 hover:bg-pink-300 flex justify-center items-center gap-x-2 rounded-md px-2 pr-3 py-1 font-semibold text-pink-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-pink-600 shadow-sm",
                    loadingThemes || loading || exporting
                      ? "cursor-not-allowed opacity-50 "
                      : "opacity-100"
                  )}
                  onClick={() => exportHandler(instoreTemplate)}
                  type="button"
                >
                  <CloudArrowDownIcon className="-ml-0.5 h-5 w-5" />
                  <p>export spreadsheet</p>
                </button>
              ) : null}

              <button
                disabled={loadingThemes || loading || exporting}
                className={classNames(
                  "bg-amber-100 hover:bg-amber-300 flex justify-center items-center gap-x-2 rounded-md px-2 pr-3 py-1 font-semibold text-amber-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber-600 shadow-sm",
                  loadingThemes || loading || exporting
                    ? "cursor-not-allowed opacity-50"
                    : "opacity-100"
                )}
                onClick={updateSpecsModalHandler}
                type="button"
              >
                <PencilSquareIcon className="-ml-0.5 h-5 w-5" />
                <p>edit specs</p>
              </button>

              <button
                disabled={loadingThemes || loading || !isDirty || exporting}
                className={classNames(
                  "bg-purple-600 hover:bg-purple-300 flex justify-center items-center gap-x-3 rounded-md px-4 pr-6 py-1 font-bold text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-800 shadow-sm",
                  loadingThemes || loading || !isDirty || exporting
                    ? "cursor-not-allowed opacity-50 "
                    : "opacity-100 animate-pulse"
                )}
                onClick={saveChangesHandler}
                type="button"
              >
                <CloudArrowUpIcon className="-ml-0.5 h-5 w-5" />
                <p>SAVE</p>
              </button>
            </div>
          }
          disabled={loadingThemes || loading || exporting}
          isAdmin={isAdmin}
          hqTeam={hqTeam}
          title={
            create
              ? "Create Instore Template"
              : copy && instoreTemplate
              ? `Copy ${instoreTemplate.name}`
              : instoreTemplate
              ? `Edit ${instoreTemplate.name}`
              : "Template detail"
          }
        >
          <GoBackButton disabled={exporting} />
        </PageHeader>

        {localTableData && localTableData.length > 0 ? (
          <InstoreTemplateDetailTable
            updateRowCellsHandler={updateRowCellsHandler}
            addParentRowHandler={addThemeRowModalClickHandler}
            addChildRowHandler={addArticleRowHandler}
            deleteRowHandler={removeRowHandler}
            availableFormats={distinctStoreFormats}
            instoreTemplate={instoreTemplate}
            disabled={loadingThemes || loading || exporting}
            moveRow={moveRowHandler}
            data={localTableData}
          />
        ) : null}
      </MainContainer>
    </Fragment>
  );
  //#endregion
}
